import { DependentFields, Field, FieldTypes, Section, ServerSideField } from './types';

export const getFieldIndexById = (fields: Section[], fieldId: string): number =>
    fields.map((f) => f.id).indexOf(fieldId);

export function reorder<T>(order: T[], startIndex: number, endIndex: number): Array<T> {
    const result = Array.from(order);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
}

const getDependentFields = (allFields: ServerSideField[]): DependentFields => {
    const allDependentFields: DependentFields = allFields.reduce((accumulator, field) => {
        const dependentId = field.metadata?.dependent_parent_id;
        if (dependentId) {
            if (accumulator[dependentId]) {
                accumulator[dependentId].push(field.id);
            } else {
                accumulator[dependentId] = [field.id];
            }
        }
        return accumulator;
    }, {});
    return Object.keys(allDependentFields).reduce((accumulator, dependentFieldId) => {
        const needle = Object.entries(allDependentFields).find(([_, list]) => list.includes(Number(dependentFieldId)));
        if (!needle) {
            return {
                ...accumulator,
                [dependentFieldId]: allDependentFields[dependentFieldId]
            };
        }
        const [targetId, targetList] = needle;
        accumulator[targetId] = targetList.flatMap((itemId) => {
            if (Number(dependentFieldId) === itemId) {
                return [itemId, ...allDependentFields[dependentFieldId]];
            }
            return itemId;
        });
        return accumulator;
    }, {});
};

const rollupDependentFields = (allFields: ServerSideField[]): ServerSideField[] => {
    const dependentFields = getDependentFields(allFields);
    const dependentFieldIds = Object.values(dependentFields).flat();

    return allFields.reduce((memo, field) => {
        if (dependentFields[field.id]) {
            return [
                ...memo,
                {
                    ...field,
                    dependent_field_ids: dependentFields[field.id]
                }
            ];
        }
        if (dependentFieldIds.includes(field.id)) {
            return memo;
        }
        return [...memo, field];
    }, []);
};

export const structuredField = ({ id, title, type, dependent_field_ids }): Field => ({
    id,
    title,
    type,
    dependentFieldIds: dependent_field_ids
});

export const structuredSection = (sectionFields: ServerSideField[], section = null): Section => {
    const sectionId = section ? section.id : 0;
    const isVisible = section ? section.is_visible : true;
    const { fields, hiddenFields } = sectionFields.reduce(
        (memo, field) => {
            if (field.hidden) {
                return memo;
            }
            if (field.is_visible === false) {
                memo.hiddenFields.push(field.id);
                if (field.dependent_field_ids) {
                    memo.hiddenFields.push(...field.dependent_field_ids);
                }
            } else {
                memo.fields.push(field);
            }
            return memo;
        },
        {
            fields: [],
            hiddenFields: []
        }
    );
    return {
        isDragDisabled: !section,
        id: `section_${sectionId}`,
        section: section && structuredField(section),
        fields: fields.map(structuredField),
        hiddenFields,
        isVisible
    };
};

const eachCons = (array, num = 2) => Array.from({ length: array.length - num + 1 }, (_, i) => array.slice(i, i + num));

const rollupSections = (allFields): Section[] => {
    const groupedFields = allFields.reduce(
        (memo, field, index) => {
            if (allFields.length - 1 === index) {
                return [...memo, index + 1];
            }
            if (field.type === FieldTypes.SECTION && field.hidden !== true) {
                return [...memo, index];
            }
            return memo;
        },
        [0]
    );
    return eachCons(groupedFields).reduce((memo, [first, last]) => {
        const sectionFields = allFields.slice(first, last);
        if (first === 0) {
            return [...memo, structuredSection(sectionFields)];
        }
        const [section, ...restSectionFields] = sectionFields;
        return [...memo, structuredSection(restSectionFields, section)];
    }, []);
};

export const getReorderableFieldIndexById = (reorderableStructure: Section[], fieldId: number): number => {
    return reorderableStructure
        .reduce((memo, section) => [...memo, section.section, ...section.fields], [])
        .filter((field) => !isNaN(field?.id))
        .map((field) => field.id)
        .indexOf(fieldId);
};

export const createReorderableStructure = (
    allFields: ServerSideField[]
): [Section[], (reorderedStructure: Section[]) => Array<number>] => {
    const normalizedFields = rollupDependentFields(allFields);
    const initialSections = rollupSections(normalizedFields);
    const visibleSections = initialSections.filter((section) => section.isVisible);
    const hiddenSections = initialSections.filter((section) => !section.isVisible);
    const toActivityFieldId = (id: number): number => {
        const field = allFields.find((f) => f.id === id);
        return Number(field.activity_field_data.field_id);
    };
    const serializeReorderedSections = (reorderedStructure: Section[]): Array<number> => {
        const resultStructure = [...reorderedStructure, ...hiddenSections];
        return resultStructure
            .reduce((memo, { section, fields, hiddenFields }) => {
                const flattenFields = fields.flatMap((f) => {
                    if (f.dependentFieldIds) {
                        return [f.id, ...f.dependentFieldIds];
                    }
                    return f.id;
                });
                return [...memo, section && section.id, ...flattenFields, ...hiddenFields];
            }, [])
            .filter(Number.isInteger)
            .map(toActivityFieldId);
    };

    return [visibleSections, serializeReorderedSections];
};
